import { Injectable } from '@angular/core';
import { EntityRelationService } from './entity-relation.service';
import { layoutCache } from '@processo/shared/components/entity-layout/smart-table/cache';

type RawFilterEntry = {
  entity_name?: string;
  key1: string;
  key2: string; // operator like $eq, $in, $btw
  key3: any; // value
  key4?: boolean;
};

@Injectable({ providedIn: 'root' })
export class GlobalFilterService {
  constructor(private relationService: EntityRelationService) {}
  private originEntityName: string | null = null;
  private rawFilterEntries: RawFilterEntry[] = [];
  private dashboardFilterJson: any = {};
  private dashboardOriginContext: string | null = null; // 'dashboard', 'entity', etc.
  private lastProcessedTarget: string | null = null; // To prevent duplicate processing
  private lastProcessedResult: Record<string, any> = {}; // Cache last result

  clear(): void {
    // quiet debug
    //console.log(`[GlobalFilter] DEBUG: clear() called - clearing all filters`);
    this.originEntityName = null;
    this.rawFilterEntries = [];
    this.dashboardFilterJson = {};
    this.dashboardOriginContext = null;
    this.lastProcessedTarget = null;
    this.lastProcessedResult = {};
  }

  /**
   * Clear only dashboard filters (keep entity filters)
   */
  clearDashboardFilters(): void {
    //console.log(`[GlobalFilter] DEBUG: clearDashboardFilters() called`);
    //console.log(`[GlobalFilter] DEBUG: Before clear:`, this.dashboardFilterJson);
    this.dashboardFilterJson = {};
    this.dashboardOriginContext = null;
    this.lastProcessedTarget = null;
    this.lastProcessedResult = {};
    //console.log(`[GlobalFilter] DEBUG: After clear:`, this.dashboardFilterJson);
  }

  getOriginEntityName(): string | null {
    return this.originEntityName;
  }

  getRawEntries(): RawFilterEntry[] {
    return [...this.rawFilterEntries];
  }

  getDashboardFilterJson(): any {
    return { ...this.dashboardFilterJson };
  }

  getDashboardOriginContext(): string | null {
    return this.dashboardOriginContext;
  }

  /**
   * Set dashboard filters that should be applied globally
   */
  setDashboardFilters(filterJson: any, context: string = 'dashboard'): void {
    
    
    this.dashboardFilterJson = filterJson || {};
    this.dashboardOriginContext = context;
    // If dashboard filters are provided, ensure entity suppression is lifted
    try {
      if (this.dashboardFilterJson && Object.keys(this.dashboardFilterJson).length > 0) {
        layoutCache.set('filtersSuppressed', false);
        // Clear any previous entity-origin filters to avoid mixing old entity selections
        this.originEntityName = null;
        this.rawFilterEntries = [];
        
      }
    } catch {}
    
    
  }

  /**
   * Manual test method - set dashboard filters for testing
   */
  testSetDashboardFilters(filterJson: any): void {
   // console.log(`[GlobalFilter] TEST: Setting test dashboard filters:`, filterJson);
    this.setDashboardFilters(filterJson, 'manual-test');
  }

  /**
   * Check if we have any active filters (entity or dashboard)
   */
  hasActiveFilters(): boolean {
    const hasEntityFilters = this.rawFilterEntries.length > 0;
    const hasDashboardFilters = this.dashboardFilterJson && Object.keys(this.dashboardFilterJson).length > 0;
    return hasEntityFilters || hasDashboardFilters;
  }

  /**
   * Capture dashboard filters from various sources (dashboard state, cache, etc.)
   * This method integrates with existing dashboard filter infrastructure
   */
  captureDashboardFilters(dashboardService: any, dashboardCache?: any): void {
    // console.log(`[GlobalFilter] DEBUG: captureDashboardFilters called`);
    // console.log(`[GlobalFilter] DEBUG: dashboardService:`, dashboardService);
    // console.log(`[GlobalFilter] DEBUG: dashboardCache:`, dashboardCache);
    
    try {
      let dashboardFilters: any = {};

      // Method 1: Try to get from dashboard service API call method
      if (dashboardService && typeof dashboardService.getFilterJsonForApiCall === 'function') {
        //console.log(`[GlobalFilter] DEBUG: Trying method 1 - getFilterJsonForApiCall`);
        const apiFilters = dashboardService.getFilterJsonForApiCall();
       // console.log(`[GlobalFilter] DEBUG: Method 1 result:`, apiFilters);
        if (apiFilters && Object.keys(apiFilters).length > 0) {
          dashboardFilters = { ...dashboardFilters, ...apiFilters };
          //console.log(`[GlobalFilter] DEBUG: Method 1 added filters:`, apiFilters);
        }
      } else {
       // console.log(`[GlobalFilter] DEBUG: Method 1 skipped - no getFilterJsonForApiCall function`);
      }

      // Method 2: Try to get from dashboard cache if provided
      if (dashboardCache && typeof dashboardCache.get === 'function') {
        //console.log(`[GlobalFilter] DEBUG: Trying method 2 - dashboard cache`);
        const cachedData = dashboardCache.get('filter_data');
        //console.log(`[GlobalFilter] DEBUG: Cached data:`, cachedData);
        if (cachedData && cachedData.filter) {
          dashboardFilters = { ...dashboardFilters, ...cachedData.filter };
          //console.log(`[GlobalFilter] DEBUG: Method 2 added filters:`, cachedData.filter);
        }
      } else {
        //console.log(`[GlobalFilter] DEBUG: Method 2 skipped - no dashboard cache or get function`);
      }

      // Method 3: Try to get from current dashboard state
      if (dashboardService && typeof dashboardService.getFilterState === 'function') {
       // console.log(`[GlobalFilter] DEBUG: Trying method 3 - getFilterState`);
        const filterState = dashboardService.getFilterState();
        //console.log(`[GlobalFilter] DEBUG: Filter state:`, filterState);
        if (Array.isArray(filterState) && filterState.length > 0) {
          const mappedFilters: any = {};
          filterState.forEach((f: any) => {
            const val = f?.default;
            if (val !== null && val !== undefined && val !== '' && (!Array.isArray(val) || val.length > 0)) {
              const key = f?.name || f?.key;
              if (key) {
                mappedFilters[key] = { $in: Array.isArray(val) ? val : [val] };
                //console.log(`[GlobalFilter] DEBUG: Method 3 mapped filter: ${key} =`, mappedFilters[key]);
              }
            }
          });
          dashboardFilters = { ...dashboardFilters, ...mappedFilters };
          //console.log(`[GlobalFilter] DEBUG: Method 3 added filters:`, mappedFilters);
        }
      } else {
        //console.log(`[GlobalFilter] DEBUG: Method 3 skipped - no getFilterState function`);
      }

     // console.log(`[GlobalFilter] DEBUG: Total captured dashboard filters:`, dashboardFilters);

      // Set the captured dashboard filters
      if (Object.keys(dashboardFilters).length > 0) {
        this.setDashboardFilters(dashboardFilters, 'dashboard-auto-captured');
        //console.log('[GlobalFilter] SUCCESS: Captured dashboard filters:', dashboardFilters);
      } else {
        //console.log('[GlobalFilter] DEBUG: No dashboard filters captured from any method');
      }
    } catch (error) {
      //console.error('[GlobalFilter] ERROR: Error capturing dashboard filters:', error);
    }
  }

  /**
   * Store the latest applied filter as the global filter.
   * Passing an empty/falsey filter will clear the global filter.
   */
  setFromEntityFilter(entries: RawFilterEntry[] | null | undefined, originEntityName: string | undefined): void {
    if (!entries || !Array.isArray(entries) || entries.length === 0 || !originEntityName) {
      
      this.clear();
      return;
    }
    this.originEntityName = originEntityName;
    // Keep only valid rows with key1 and operator
    this.rawFilterEntries = entries.filter((e) => e && e.key1 && (e.key2 !== undefined && e.key2 !== null));
    
  }

  /**
   * Build a filter object suitable to be merged into payload.filter for a target entity.
   * Implements parent-child filter propagation: child → parent → all parent's children
   * Now includes dashboard filters with the same propagation logic
   */
  buildFilterForEntity(targetEntityName: string, metadata: any): Record<string, any> {
    // console.log(`[GlobalFilter] DEBUG: buildFilterForEntity called for target: ${targetEntityName}`);
    // console.log(`[GlobalFilter] DEBUG: originEntityName: ${this.originEntityName}`);
    // console.log(`[GlobalFilter] DEBUG: rawFilterEntries count: ${this.rawFilterEntries.length}`);
    // console.log(`[GlobalFilter] DEBUG: dashboardFilterJson:`, this.dashboardFilterJson);
    // console.log(`[GlobalFilter] DEBUG: dashboardOriginContext: ${this.dashboardOriginContext}`);
    
    // Check if we've already processed this target recently (prevent duplicate calls)
    const cacheKey = `${targetEntityName}_${JSON.stringify(this.dashboardFilterJson)}_${this.rawFilterEntries.length}_${this.originEntityName}`;
    if (this.lastProcessedTarget === cacheKey) {
      //console.log(`[GlobalFilter] DEBUG: Using cached result for ${targetEntityName}`);
      return { ...this.lastProcessedResult };
    }
    
    let result: Record<string, any> = {};
    
    // Apply entity-based filters (existing logic)
    if (this.originEntityName && this.rawFilterEntries.length > 0) {
      //console.log(`[GlobalFilter] DEBUG: Building entity filters...`);
      const entityFilters = this.buildEntityFilters(targetEntityName, metadata);
      //console.log(`[GlobalFilter] DEBUG: Entity filters result:`, entityFilters);
      result = { ...result, ...entityFilters };
    } else {
      //console.log(`[GlobalFilter] DEBUG: Skipping entity filters - no origin entity or entries`);
    }
    
    // Apply dashboard filters with parent-child propagation
    if (this.dashboardFilterJson && Object.keys(this.dashboardFilterJson).length > 0) {
     // console.log(`[GlobalFilter] DEBUG: Building dashboard filters...`);
      const dashboardFilters = this.buildDashboardFilters(targetEntityName, metadata);
      //console.log(`[GlobalFilter] DEBUG: Dashboard filters result:`, dashboardFilters);
      result = { ...result, ...dashboardFilters };
    } else {
      //console.log(`[GlobalFilter] DEBUG: Skipping dashboard filters - no dashboard filters set`);
    }
    
    //console.log(`[GlobalFilter] DEBUG: Final combined result:`, result);
    
    // Cache the result
    this.lastProcessedTarget = cacheKey;
    this.lastProcessedResult = { ...result };
    
    return result;
  }

  /**
   * Build entity-based filters (original logic extracted)
   */
  private buildEntityFilters(targetEntityName: string, metadata: any): Record<string, any> {
    const result: Record<string, any> = {};
    
    if (!this.originEntityName || this.rawFilterEntries.length === 0) {
      return result;
    }

    const entities: any[] = metadata?.entities || [];
    const targetEntity = entities.find((e: any) => e?.name === targetEntityName);
    if (!targetEntity) {
      return result;
    }

    // Helpers
    const findEntity = (name: string) => (metadata?.entities || []).find((e: any) => e?.name === name);
    const hasField = (entity: any, fieldName: string) => !!entity?.fields?.some((f: any) => f?.name === fieldName);
    const childFksTo = (child: any, parentName: string) => (child?.fields || []).filter(
      (f: any) => (f?.datatype === 'dropdown_single_select' || f?.datatype === 'dropdown_multi_select') && f?.option_type === 'ENTITY_DATA' && f?.entity_name === parentName
    );
    const getParentEntities = (entity: any) => entity?.p_entities || [];
    const getCommonParents = (entity1: any, entity2: any) => {
      const parents1 = getParentEntities(entity1);
      const parents2 = getParentEntities(entity2);
      return parents1.filter((p: string) => parents2.includes(p));
    };

    const originEntity = entities.find((e: any) => e?.name === this.originEntityName);

    // PRIORITY 1: If target entity is the origin, apply filters directly on its fields
    if (targetEntityName === this.originEntityName) {
      for (const entry of this.rawFilterEntries) {
        if (!entry.key1) continue;
        const operator = entry.key2 || '$eq';
        
        // Handle composite keys properly for origin entity
        if (entry.key1.includes('~')) {
          // For composite keys on origin entity, use the full composite key
          result[entry.key1] = operator === '' ? entry.key3 : { [operator]: entry.key3 };
        } else {
          // For regular fields on origin entity
          result[entry.key1] = operator === '' ? entry.key3 : { [operator]: entry.key3 };
        }
      }
      return result;
    }

    // PRIORITY 2: Parent-Child Filter Propagation
    // Check if target is a parent of origin (child -> parent navigation)
    const originParents = getParentEntities(originEntity);
    const isTargetParentOfOrigin = originParents.includes(targetEntityName);
    
    if (isTargetParentOfOrigin) {
      // Child -> Parent navigation: extract field names from composite keys
      for (const entry of this.rawFilterEntries) {
        if (!entry.key1) continue;
        const operator = entry.key2 || '$eq';
        
        if (entry.key1.includes('~')) {
          // Extract the actual field name from composite key
          const [fkName, fieldName] = entry.key1.split('~');
          
          // Apply directly to parent if it has the field
          if (hasField(targetEntity, fieldName)) {
            result[fieldName] = operator === '' ? entry.key3 : { [operator]: entry.key3 };
          }
        } else {
          // Regular field - apply directly if target has it
          if (hasField(targetEntity, entry.key1)) {
            result[entry.key1] = operator === '' ? entry.key3 : { [operator]: entry.key3 };
          }
        }
      }
      
      if (Object.keys(result).length > 0) {
        return result;
      }
    }
    
    // Check if origin and target share common parent entities (sibling navigation)
    const commonParents = getCommonParents(originEntity, targetEntity);
    if (commonParents.length > 0) {
      for (const entry of this.rawFilterEntries) {
        if (!entry.key1) continue;
        const operator = entry.key2 || '$eq';
        
        if (entry.key1.includes('~')) {
          // Handle composite keys: extract the actual field name
          const [fkName, fieldName] = entry.key1.split('~');
          
          // Check if target entity can filter by the same field directly
          if (hasField(targetEntity, fieldName)) {
            // Apply the filter directly on the target field when it exists there
            result[fieldName] = operator === '' ? entry.key3 : { [operator]: entry.key3 };
          } else {
            // Try to find a path through common parents
            for (const parentName of commonParents) {
              const parent = findEntity(parentName);
              if (parent && hasField(parent, fieldName)) {
                // Create composite key through target's FK to the common parent
                const targetFksToParent = childFksTo(targetEntity, parentName);
                for (const fk of targetFksToParent) {
                  const compositeKey = `${fk.name}~${fieldName}`;
                  result[compositeKey] = operator === '' ? entry.key3 : { [operator]: entry.key3 };
                }
              }
            }
          }
        } else {
          // For regular fields, check if target has the same field
          if (hasField(targetEntity, entry.key1)) {
            // Both entities have the same field - apply directly
            result[entry.key1] = operator === '' ? entry.key3 : { [operator]: entry.key3 };
          } else {
            // Try to propagate through common parents
            for (const parentName of commonParents) {
              const parent = findEntity(parentName);
              if (parent && hasField(parent, entry.key1)) {
                // Create composite key through target's FK to the common parent
                const targetFksToParent = childFksTo(targetEntity, parentName);
                for (const fk of targetFksToParent) {
                  const compositeKey = `${fk.name}~${entry.key1}`;
                  result[compositeKey] = operator === '' ? entry.key3 : { [operator]: entry.key3 };
                }
              }
            }
          }
        }
      }
      
      if (Object.keys(result).length > 0) {
        return result;
      }
    }

    // PRIORITY 3: Check if target entity has FK fields pointing to origin entity (child -> parent)
    const foreignKeyFields = (targetEntity.fields || []).filter(
      (f: any) =>
        (f?.datatype === 'dropdown_single_select' || f?.datatype === 'dropdown_multi_select') &&
        f?.option_type === 'ENTITY_DATA' &&
        f?.entity_name === this.originEntityName
    );

    if (foreignKeyFields.length > 0) {
      for (const entry of this.rawFilterEntries) {
        if (!entry.key1) continue;
        const operator = entry.key2 || '$eq';
        
        if (entry.key1.includes('~')) {
          // Handle composite keys from origin - check if they can be applied to target
          const [fkName, fieldName] = entry.key1.split('~');
          
          // Check if this composite key can be applied directly to target
          if (hasField(targetEntity, entry.key1)) {
            result[entry.key1] = operator === '' ? entry.key3 : { [operator]: entry.key3 };
          } else {
            // Try to map through FK fields
            for (const fk of foreignKeyFields) {
              const compositeKey = `${fk.name}~${fieldName}`;
              result[compositeKey] = operator === '' ? entry.key3 : { [operator]: entry.key3 };
            }
          }
        } else {
          // For regular fields, create composite keys using FK fields
          for (const fk of foreignKeyFields) {
            const compositeKey = `${fk.name}~${entry.key1}`;
            result[compositeKey] = operator === '' ? entry.key3 : { [operator]: entry.key3 };
          }
        }
      }
      if (Object.keys(result).length > 0) {
        return result;
      }
    }

    // PRIORITY 4: Check if origin entity has FK fields pointing to target entity (parent -> child)
    const originFksToTarget = childFksTo(originEntity, targetEntityName);
    if (originFksToTarget && originFksToTarget.length > 0) {
      for (const entry of this.rawFilterEntries) {
        if (!entry.key1) continue;
        const operator = entry.key2 || '$eq';
        
        if (entry.key1.includes('~')) {
          // Handle composite keys `${fk}~${parentField}`
          const [fkName, parentField] = entry.key1.split('~');
          const isValidFk = originFksToTarget.some((f: any) => f.name === fkName);
          if (isValidFk && hasField(targetEntity, parentField)) {
            result[parentField] = operator === '' ? entry.key3 : { [operator]: entry.key3 };
          }
        } else {
          // For regular fields, check if target has this field directly
          if (hasField(targetEntity, entry.key1)) {
            result[entry.key1] = operator === '' ? entry.key3 : { [operator]: entry.key3 };
          }
        }
      }
      if (Object.keys(result).length > 0) {
        return result;
      }
    }

    // PRIORITY 5: Handle complex relationships via common ancestors
    const ancestorConstraints: Array<{ ancestorName: string; ancestorField: string; operator: string; value: any }> = [];
    for (const entry of this.rawFilterEntries) {
      if (entry.key1 && entry.key1.includes('~')) {
        const [fkName, parentField] = entry.key1.split('~');
        const fkField = (originEntity?.fields || []).find((f: any) => f.name === fkName && f?.option_type === 'ENTITY_DATA');
        const ancestorName = fkField?.entity_name;
        if (ancestorName && hasField(findEntity(ancestorName), parentField)) {
          ancestorConstraints.push({ ancestorName, ancestorField: parentField, operator: entry.key2 || '$eq', value: entry.key3 });
        }
      }
    }

    if (ancestorConstraints.length > 0) {
      for (const ac of ancestorConstraints) {
        if (targetEntityName === ac.ancestorName) {
          // Direct application to ancestor entity
          result[ac.ancestorField] = ac.operator === '' ? ac.value : { [ac.operator]: ac.value };
        } else {
          // Check if target has FK to this ancestor
          const fkToAncestor = childFksTo(targetEntity, ac.ancestorName);
          if (fkToAncestor && fkToAncestor.length > 0) {
            for (const fk of fkToAncestor) {
              const compositeKey = `${fk.name}~${ac.ancestorField}`;
              result[compositeKey] = ac.operator === '' ? ac.value : { [ac.operator]: ac.value };
            }
          }
        }
      }
      if (Object.keys(result).length > 0) {
        return result;
      }
    }

    return result;
  }

  /**
   * Build dashboard filters with parent-child propagation
   */
  private buildDashboardFilters(targetEntityName: string, metadata: any): Record<string, any> {
    // console.log(`[GlobalFilter] DEBUG: buildDashboardFilters for target: ${targetEntityName}`);
    // console.log(`[GlobalFilter] DEBUG: Dashboard filters to process:`, this.dashboardFilterJson);
    
    const result: Record<string, any> = {};
    
    if (!this.dashboardFilterJson || Object.keys(this.dashboardFilterJson).length === 0) {
     // console.log(`[GlobalFilter] DEBUG: No dashboard filters to process`);
      return result;
    }

    const entities: any[] = metadata?.entities || [];
    const targetEntity = entities.find((e: any) => e?.name === targetEntityName);
    if (!targetEntity) {
      //console.log(`[GlobalFilter] DEBUG: Target entity not found in metadata: ${targetEntityName}`);
      return result;
    }
    
    //console.log(`[GlobalFilter] DEBUG: Target entity found:`, { name: targetEntity.name, p_entities: targetEntity.p_entities });

    // Helpers (same as entity filters)
    const findEntity = (name: string) => (metadata?.entities || []).find((e: any) => e?.name === name);
    const hasField = (entity: any, fieldName: string) => !!entity?.fields?.some((f: any) => f?.name === fieldName);
    const childFksTo = (child: any, parentName: string) => (child?.fields || []).filter(
      (f: any) => (f?.datatype === 'dropdown_single_select' || f?.datatype === 'dropdown_multi_select') && f?.option_type === 'ENTITY_DATA' && f?.entity_name === parentName
    );
    const getParentEntities = (entity: any) => entity?.p_entities || [];

    // Apply dashboard filters to target entity using parent-child propagation
    Object.keys(this.dashboardFilterJson).forEach(filterKey => {
      const filterValue = this.dashboardFilterJson[filterKey];
     // console.log(`[GlobalFilter] DEBUG: Processing dashboard filter: ${filterKey} =`, filterValue);
      
      if (filterKey.includes('~')) {
        //console.log(`[GlobalFilter] DEBUG: Processing composite key: ${filterKey}`);
        // Handle composite keys from dashboard
        const [fkName, fieldName] = filterKey.split('~');
        //console.log(`[GlobalFilter] DEBUG: Split composite key - FK: ${fkName}, Field: ${fieldName}`);
        
        // If target entity has the field directly, apply it
        if (hasField(targetEntity, fieldName)) {
         // console.log(`[GlobalFilter] DEBUG: Target entity has field ${fieldName} directly`);
          // Choose correct operator based on field type in the target entity
          const correctedValue = this.correctOperatorForValue(filterValue, fieldName, targetEntity.name, metadata);
          // Apply directly to the field on target entity (flatten composite)
          result[fieldName] = correctedValue;
          //console.log(`[GlobalFilter] DEBUG: Applied direct field: ${fieldName} with operator:`, correctedValue);
        } else {
          //console.log(`[GlobalFilter] DEBUG: Target entity doesn't have field ${fieldName}, trying propagation`);
          // Try to create appropriate composite key for target entity
          // Find entities that have this field and see if target relates to them
          for (const entity of entities) {
            if (hasField(entity, fieldName)) {
              //console.log(`[GlobalFilter] DEBUG: Entity ${entity.name} has field ${fieldName}`);
              const targetFksToEntity = childFksTo(targetEntity, entity.name);
             // console.log(`[GlobalFilter] DEBUG: Target FKs to ${entity.name}:`, targetFksToEntity.map((f: any) => f.name));
              for (const fk of targetFksToEntity) {
                const compositeKey = `${fk.name}~${fieldName}`;
                result[compositeKey] = filterValue;
                //console.log(`[GlobalFilter] DEBUG: Created composite key: ${compositeKey}`);
              }
            }
          }
        }
      } else {
        //console.log(`[GlobalFilter] DEBUG: Processing regular field: ${filterKey}`);
        // Handle regular field filters
        if (hasField(targetEntity, filterKey)) {
         // console.log(`[GlobalFilter] DEBUG: Target entity has field ${filterKey} directly`);
          // Choose correct operator based on field type in the target entity
          const correctedValue = this.correctOperatorForValue(filterValue, filterKey, targetEntity.name, metadata);
          result[filterKey] = correctedValue;
        } else {
          //console.log(`[GlobalFilter] DEBUG: Target entity doesn't have field ${filterKey}, trying propagation`);
          // Try to propagate through parent-child relationships
          // Find entities that have this field
          for (const entity of entities) {
            if (hasField(entity, filterKey)) {
             // console.log(`[GlobalFilter] DEBUG: Entity ${entity.name} has field ${filterKey}`);
              // Check if target entity can reach this entity through FK
              const targetFksToEntity = childFksTo(targetEntity, entity.name);
              //console.log(`[GlobalFilter] DEBUG: Target FKs to ${entity.name}:`, targetFksToEntity.map((f: any) => f.name));
              
              // Create composite keys, but avoid duplicates
              const createdKeys = new Set<string>();
              
              for (const fk of targetFksToEntity) {
                const compositeKey = `${fk.name}~${filterKey}`;
                if (!createdKeys.has(compositeKey) && !result[compositeKey]) {
                  // Choose correct operator based on field type in the entity that has the field
                  const correctedValue = this.correctOperatorForValue(filterValue, filterKey, entity.name, metadata);
                  result[compositeKey] = correctedValue;
                  createdKeys.add(compositeKey);
                 // console.log(`[GlobalFilter] DEBUG: Created composite key via FK: ${compositeKey} with operator:`, correctedValue);
                }
              }
              
              // Also check if this entity is a parent of target (reverse relationship)
              const targetParents = getParentEntities(targetEntity);
              //console.log(`[GlobalFilter] DEBUG: Target parents:`, targetParents);
              if (targetParents.includes(entity.name)) {
                //console.log(`[GlobalFilter] DEBUG: ${entity.name} is a parent of target`);
                // Target is child of entity that has the field - create composite key
                const targetFksToParent = childFksTo(targetEntity, entity.name);
                //console.log(`[GlobalFilter] DEBUG: Target FKs to parent ${entity.name}:`, targetFksToParent.map((f: any) => f.name));
                for (const fk of targetFksToParent) {
                  const compositeKey = `${fk.name}~${filterKey}`;
                  if (!createdKeys.has(compositeKey) && !result[compositeKey]) {
                    // Choose correct operator based on field type in the parent entity
                    const correctedValue = this.correctOperatorForValue(filterValue, filterKey, entity.name, metadata);
                    result[compositeKey] = correctedValue;
                    createdKeys.add(compositeKey);
                   // console.log(`[GlobalFilter] DEBUG: Created composite key via parent: ${compositeKey} with operator:`, correctedValue);
                  } else if (createdKeys.has(compositeKey)) {
                    //console.log(`[GlobalFilter] DEBUG: Skipping duplicate composite key: ${compositeKey}`);
                  }
                }
              }
            }
          }
        }
      }
    });

    //console.log(`[GlobalFilter] DEBUG: Dashboard filters processing complete. Result:`, result);

    return result;
  }

  /**
   * Sync entity filters back to dashboard service
   * This enables bidirectional filter synchronization
   */
  syncEntityFiltersToDashboard(dashboardService: any, entityName: string, metadata: any): void {
    if (!this.originEntityName || this.rawFilterEntries.length === 0) {
      return;
    }

    try {
      // Convert entity filters to dashboard filter format
      const dashboardFilters: any[] = [];
      
      for (const entry of this.rawFilterEntries) {
        if (!entry.key1) continue;
        
        // Find the corresponding dashboard filter configuration
        const dashboardFilter = this.findDashboardFilterByName(entry.key1, dashboardService);
        if (dashboardFilter) {
          // Update the dashboard filter with the entity filter value
          const updatedFilter = {
            ...dashboardFilter,
            default: this.convertEntityValueToDashboardValue(entry, dashboardFilter)
          };
          dashboardFilters.push(updatedFilter);
        }
      }

      // Update dashboard service with the new filter values
      if (dashboardFilters.length > 0 && dashboardService) {
        // Check if dashboard service has valid dashboard data
        const currentDashboard = dashboardService.currentDashBoard?.();
        const hasValidDashboard = currentDashboard && 
                                 currentDashboard.dashboard && 
                                 currentDashboard.dashboard.filters && 
                                 Array.isArray(currentDashboard.dashboard.filters);

        if (hasValidDashboard) {
          // Dashboard data is available - apply filters immediately
          if (typeof dashboardService.updateDashboardFiltersFromExternal === 'function') {
            dashboardService.updateDashboardFiltersFromExternal(dashboardFilters);
          } else {
            // Fallback to direct update if method doesn't exist
            const existingFilters = currentDashboard.dashboard.filters;
            const mergedFilters = this.mergeDashboardFilters(existingFilters, dashboardFilters);
            
            dashboardService.currentDashBoard.update((prev: any) => {
              if (prev) {
                return {
                  ...prev,
                  dashboard: {
                    ...prev.dashboard,
                    filters: mergedFilters
                  }
                };
              }
              return prev;
            });

            // Update filter state in service
            dashboardService.updateFilterState(mergedFilters);
            
            // Store the updated state
            const dashboardKey = `${currentDashboard.org_id}_${currentDashboard.process_id}_${currentDashboard.org_process_did}`;
            dashboardService.storeFilterState(dashboardKey, mergedFilters);
          }
          //console.log('[GlobalFilter] Synced entity filters to dashboard immediately:', dashboardFilters);
        } else {
          // Dashboard data is not available - store as pending filters
          const dashboardKey = this.constructDashboardKey(dashboardService);
          //console.log('[GlobalFilter] Constructed dashboard key for pending filters:', dashboardKey);
          if (dashboardKey) {
            dashboardService.storePendingEntityFilters(dashboardKey, dashboardFilters);
           // console.log('[GlobalFilter] Stored entity filters as pending for dashboard:', dashboardKey, dashboardFilters);
          } else {
            //console.warn('[GlobalFilter] Cannot determine dashboard key for pending filters');
          }
        }
      }
    } catch (error) {
      console.error('[GlobalFilter] Error syncing entity filters to dashboard:', error);
    }
  }

  /**
   * Find dashboard filter by name (supports composite keys)
   */
  private findDashboardFilterByName(filterName: string, dashboardService: any): any {
    try {
      const currentDashboard = dashboardService.currentDashBoard?.();
      if (!currentDashboard?.dashboard?.filters) {
        //console.log(`[GlobalFilter] No dashboard filters available for ${filterName}`);
        return null;
      }

      const dashboardFilters = currentDashboard.dashboard.filters;
     // console.log(`[GlobalFilter] Looking for filter ${filterName} in dashboard filters:`, dashboardFilters.map((f: any) => ({ name: f.name, key: f.key })));
      
      // Handle composite keys (entity~field format)
      if (filterName.includes('~')) {
        const [entityName, fieldName] = filterName.split('~');
        const found = dashboardFilters.find((f: any) => f.name === fieldName || f.key === fieldName);
        //console.log(`[GlobalFilter] Composite key search for ${fieldName}:`, found ? 'FOUND' : 'NOT FOUND');
        return found;
      }
      
      // Handle regular field names
      const found = dashboardFilters.find((f: any) => f.name === filterName || f.key === filterName);
     //console.log(`[GlobalFilter] Regular key search for ${filterName}:`, found ? 'FOUND' : 'NOT FOUND');
      return found;
    } catch (error) {
      console.error('[GlobalFilter] Error finding dashboard filter:', error);
      return null;
    }
  }

  /**
   * Convert entity filter value to dashboard filter value format
   */
  private convertEntityValueToDashboardValue(entityEntry: RawFilterEntry, dashboardFilter: any): any {
    const { key2: operator, key3: value } = entityEntry;
    
    // Handle different dashboard filter types
    switch (dashboardFilter.type) {
      case 'dropdown_single_select':
      case 'dropdown_multi_select':
        if (operator === '$in' || operator === '$jin') {
          return Array.isArray(value) ? value : [value];
        }
        return Array.isArray(value) ? value : [value];
        
      case 'checkbox':
        if (operator === 'true' || value === true) return true;
        if (operator === 'false' || value === false) return false;
        return Boolean(value);
        
      case 'date_range':
        if (operator === '$btw' && Array.isArray(value) && value.length === 2) {
          return value;
        }
        return [value, value]; // Single date as range
        
      case 'number':
      case 'currency':
        return Array.isArray(value) ? value[0] : value;
        
      default:
        return value;
    }
  }

  /**
   * Create a new dashboard filter from entity entry if it doesn't exist
   */
  private createDashboardFilterFromEntityEntry(entry: RawFilterEntry, entityName: string, metadata: any): any | null {
    try {
      const { key1: fieldName, key2: operator, key3: value } = entry;
      
      // Find the field definition in metadata
      const entities = metadata?.entities || [];
      const entity = entities.find((e: any) => e?.name === entityName);
      if (!entity) return null;
      
      const field = entity.fields?.find((f: any) => f?.name === fieldName);
      if (!field) return null;
      
      // Create dashboard filter based on field type
      const dashboardFilter: any = {
        name: fieldName,
        key: fieldName,
        type: this.mapEntityFieldTypeToDashboardType(field.datatype),
        default: this.convertEntityValueToDashboardValue(entry, { type: field.datatype }),
        position: 999, // Default position for new filters
        required: false,
        visible: true
      };
      
      // Add field-specific properties
      if (field.options && Array.isArray(field.options)) {
        dashboardFilter.options = field.options;
      }
      
      return dashboardFilter;
    } catch (error) {
      console.error('[GlobalFilter] Error creating dashboard filter from entity entry:', error);
      return null;
    }
  }

  /**
   * Map entity field datatype to dashboard filter type
   */
  private mapEntityFieldTypeToDashboardType(datatype: string): string {
    const typeMapping: any = {
      'dropdown_single_select': 'dropdown_single_select',
      'dropdown_multi_select': 'dropdown_multi_select',
      'textbox': 'textbox',
      'textarea': 'textarea',
      'number': 'number',
      'currency': 'currency',
      'date': 'date',
      'checkbox': 'checkbox',
      'status': 'dropdown_single_select',
      'labels': 'dropdown_multi_select',
      'people': 'dropdown_multi_select',
      'location': 'textbox',
      'website': 'textbox',
      'phone': 'textbox',
      'email': 'textbox',
      'progress': 'number',
      'rating': 'number',
      'tag': 'dropdown_multi_select',
      'time': 'textbox'
    };
    
    return typeMapping[datatype] || 'textbox';
  }

  /**
   * Construct dashboard key when dashboard data is not available
   */
  private constructDashboardKey(dashboardService: any): string | null {
    try {
      //console.log('[GlobalFilter] Constructing dashboard key...');
      
      // Try to get from session storage or other sources
      const lastDashboardData = sessionStorage.getItem('dbf::last');
      //console.log('[GlobalFilter] Last dashboard data from session:', lastDashboardData);
      
      if (lastDashboardData) {
        const parsed = JSON.parse(lastDashboardData);
        //console.log('[GlobalFilter] Parsed dashboard data:', parsed);
        if (parsed.meta && parsed.meta.org_id && parsed.meta.process_id) {
          const key = `${parsed.meta.org_id}_${parsed.meta.process_id}_${parsed.meta.org_process_did || 'undefined'}`;
          //console.log('[GlobalFilter] Generated key from session data:', key);
          return key;
        }
      }
      
      // Try to get from current dashboard signal (even if null)
      const currentDashboard = dashboardService.currentDashBoard?.();
      //console.log('[GlobalFilter] Current dashboard data:', currentDashboard);
      
      if (currentDashboard && currentDashboard.org_id && currentDashboard.process_id) {
        const key = `${currentDashboard.org_id}_${currentDashboard.process_id}_${currentDashboard.org_process_did || 'undefined'}`;
        //console.log('[GlobalFilter] Generated key from current dashboard:', key);
        return key;
      }
      
      //console.log('[GlobalFilter] Could not construct dashboard key');
      return null;
    } catch (error) {
      console.error('[GlobalFilter] Error constructing dashboard key:', error);
      return null;
    }
  }

  /**
   * Merge updated filters with existing dashboard filters
   */
  private mergeDashboardFilters(existingFilters: any[], updatedFilters: any[]): any[] {
    const result = [...existingFilters];
    
    for (const updatedFilter of updatedFilters) {
      const existingIndex = result.findIndex((f: any) => 
        f.name === updatedFilter.name || f.key === updatedFilter.key
      );
      
      if (existingIndex >= 0) {
        // Update existing filter
        result[existingIndex] = updatedFilter;
      } else {
        // Add new filter
        result.push(updatedFilter);
      }
    }

    return result;
  }

  /**
   * Apply correct operator based on field type using the same logic as drilldown
   * Uses the filter_operator mapping from AppStateService
   */
  private correctOperatorForValue(filterValue: any, fieldName: string, entityName: string, metadata: any): any {
    if (!filterValue || typeof filterValue !== 'object') {
      return filterValue;
    }

    // Find the field definition in metadata
    const entities = metadata?.entities || [];
    const entity = entities.find((e: any) => e?.name === entityName);
    if (!entity) {
     // console.log(`[GlobalFilter] DEBUG: Entity ${entityName} not found in metadata, preserving operators`);
      return filterValue;
    }

    const field = entity.fields?.find((f: any) => f?.name === fieldName);
    if (!field) {
     // console.log(`[GlobalFilter] DEBUG: Field ${fieldName} not found in entity ${entityName}, preserving operators`);
      return filterValue;
    }

    const fieldType = field.datatype;
    //console.log(`[GlobalFilter] DEBUG: Field ${fieldName} in entity ${entityName} has type: ${fieldType}`);

    // Use the same filter_operator mapping as the drilldown logic
    const filterOperatorMapping: any = {
      dropdown_single_select: '$in',
      dropdown_multi_select: '$jin',
      status: '$in',
      location: '',
      textarea: '$like',
      checkbox: 'true',
      rating: '',
      currency: '',
      number: '',
      labels: '$in',
      website: '',
      people: '$jin',
      phone: '',
      progress: '',
      date: '',
      textbox: '$in',
      email: '',
      tag: '$jin',
      time: '',
    };

    const correctOperator = filterOperatorMapping[fieldType];
    //console.log(`[GlobalFilter] DEBUG: Correct operator for field type ${fieldType}: ${correctOperator}`);

    // If we don't have a specific operator for this field type, preserve original
    if (!correctOperator) {
      //console.log(`[GlobalFilter] DEBUG: No specific operator mapping for field type ${fieldType}, preserving original`);
      return filterValue;
    }

    // Apply the correct operator based on field type
    const result: any = {};
    Object.keys(filterValue).forEach(operator => {
      const value = filterValue[operator];
      //console.log(`[GlobalFilter] DEBUG: Processing operator ${operator} with value:`, value);
      
      if (operator === '$in' || operator === '$jin') {
        // Use the correct operator for this field type
        result[correctOperator] = value;
        //console.log(`[GlobalFilter] DEBUG: Changed ${operator} -> ${correctOperator} for field ${fieldName} (type: ${fieldType})`);
      } else {
        // Keep other operators as-is
        result[operator] = value;
       // console.log(`[GlobalFilter] DEBUG: Keeping operator ${operator} as-is`);
      }
    });

    //console.log(`[GlobalFilter] DEBUG: Final result for field ${fieldName}:`, result);

    return result;
  }


}


